import BaseService from "@/services/BaseService.ts";
import {supabase} from "@supabaseClient";
import {Bank} from "@/integrations/supabase/account.models.ts";

/**
 * Bank Service manages bank operations
 */
class BankService extends BaseService {

    /**
     * Creates a new bank
     * @param {BankCreateRequest} request request to create a new bank
     * @returns {Bank} retrieved bank
     */
    async createBank(request: BankCreateRequest) : Promise<Bank> {
        const { data, error } = await supabase
            .from('banks')
            .insert(request)
            .select()
            .single();
        if (error) {
            await this.handleError(error);
            throw error;
        }
        return data
    }

    /**
     * Retrieves a bank given its ID
     * @param {string} id Bank ID to retrieve
     * @returns {Bank} retrieved bank
     */
    async fetchBankById(id: string) : Promise<Bank> {
        const { data, error } = await supabase
            .from('banks')
            .select()
            .eq('id', id)
            .single();

        if (error) {
            await this.handleError(error);
            throw error
        }

        return data
    }

    /**
     * Fetches all banks
     */
    async fetchAllBanks() : Promise<Bank[]> {
        const { data, error } = await supabase
            .from('banks')
            .select('*')
            .order('name', { ascending: true });

        if (error) {
            await this.handleError(error);
            throw error
        }

        return data
    }

    /**
     * Updates a bank address given its ID and returns it
     * @param {string} id Bank ID to update
     * @param {string} address new address of the bank
     */
    async updateBankAddress(id: string, address: string) : Promise<Bank> {
        const { data, error } = await supabase
            .from('banks')
            .update({ address: address })
            .eq('id', id)
            .select()
            .single();

        if (error) {
            await this.handleError(error);
            throw error
        }

        return data
    }
}

export default new BankService();
